import { useEffect, useState } from 'react';
import { Cpu } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { aiApi, selectedProvider } from '../../api/ai';
import type { AiSettingsResponse, AiModel } from '../../api/ai';

export function AiSettings() {
  const [settings, setSettings] = useState<AiSettingsResponse | null>(null);
  const [models, setModels] = useState<AiModel[]>([]);
  const [provider, setProvider] = useState('commandcode');
  const [apiKey, setApiKey] = useState('');
  const [modelId, setModelId] = useState('');
  const [freeOnly, setFreeOnly] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState('');

  useEffect(() => {
    aiApi.getSettings().then((res) => {
      const s = res.data;
      setSettings(s);
      setProvider(selectedProvider(s.provider));
      setModelId(s.model_id || '');
      setFreeOnly(s.free_only);
      setEnabled(s.enabled);
    }).catch(() => {});
    aiApi.getModels().then((res) => {
      setModels(res.data.models || []);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!apiKey.trim() && !settings?.has_key) {
      setError('Enter a provider API key. This server has no platform default key.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await aiApi.saveSettings({
        provider: selectedProvider(provider),
        api_key: apiKey || undefined,
        model_id: modelId,
        free_only: freeOnly,
        enabled,
      });
      setSuccess('AI settings saved successfully');
      setApiKey('');
      // Refresh settings
      const res = await aiApi.getSettings();
      setSettings(res.data);
      // Refresh models for the new provider
      const modelsRes = await aiApi.getModels();
      setModels(modelsRes.data.models || []);
    } catch (err: unknown) {
      const saveError = err as { response?: { data?: { message?: string } } };
      setError(saveError.response?.data?.message || 'Failed to save AI settings');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await aiApi.deleteSettings();
      setSuccess('AI settings reset to platform defaults');
      const res = await aiApi.getSettings();
      const s = res.data;
      setSettings(s);
      setProvider(selectedProvider(s.provider));
      setModelId(s.model_id || '');
      setFreeOnly(s.free_only);
      setEnabled(s.enabled);
    } catch {
      setError('Failed to reset AI settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setTestResult('');
    try {
      const res = await aiApi.testConnection();
      setTestResult(res.data.message);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setTestResult(e.response?.data?.message || 'Connection test failed');
    }
  };

  const handleProviderChange = (newProvider: string) => {
    setProvider(newProvider);
    setModelId('');
    setTestResult('');
    setError('');
    setSuccess('');
  };

  return (
    <Card>
      <div className="flex items-center gap-4 mb-6">
        <div className="p-3 bg-purple-50 rounded-full">
          <Cpu className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">AI Configuration</h3>
          <p className="text-sm text-gray-500">
            Configure AI providers for campaign design, smart grouping, and assistance.
            {settings?.source === 'platform' && (
              <span className="ml-1 text-indigo-600">(Using platform defaults)</span>
            )}
          </p>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-50 text-green-700 rounded-lg mb-4 text-sm">
          {success}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="label" htmlFor="ai-provider">Provider</label>
          <select
            id="ai-provider"
            className="input"
            value={provider}
            onChange={(e) => handleProviderChange(e.target.value)}
          >
            <option value="commandcode">CommandCode (free models available)</option>
            <option value="deepseek">DeepSeek (cheapest: deepseek-flash)</option>
            <option value="openai">OpenAI</option>
            <option value="anthropic">Anthropic</option>
          </select>
        </div>

        <div>
          <label className="label" htmlFor="ai-model">Model</label>
          {models.length > 0 ? (
            <select
              id="ai-model"
              className="input"
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
            >
              <option value="">Use provider default</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          ) : (
            <input
              id="ai-model"
              type="text"
              className="input"
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              placeholder={provider === 'commandcode' ? 'poolside/laguna-s-2.1-free' : provider === 'deepseek' ? 'deepseek-flash' : 'Model name'}
            />
          )}
          <p className="text-sm text-gray-500 mt-1">
            {provider === 'commandcode' && 'Free models: poolside/laguna-s-2.1-free, inclusionai/ling-3.0-flash-sante:free, inclusionai/ling-3.1-flash:free'}
            {provider === 'deepseek' && 'Cheapest model: deepseek-flash ($0.15/M input, $0.6/M output)'}
          </p>
        </div>

        <div>
          <label className="label" htmlFor="ai-api-key">
            API Key {settings?.has_key && <span className="text-green-600 text-xs">(configured)</span>}
          </label>
          <input
            id="ai-api-key"
            type="password"
            className="input"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={settings?.has_key ? 'Leave empty to keep current key' : 'Enter your provider API key'}
            autoComplete="new-password"
          />
          <p className="text-sm text-gray-500 mt-1">
            {settings?.has_key
              ? 'Leave empty to keep the current key.'
              : 'Required. This server has no platform default key.'}
          </p>
        </div>

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={freeOnly}
            onChange={(e) => setFreeOnly(e.target.checked)}
            className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
          />
          <div>
            <p className="font-medium text-gray-900">Free models only</p>
            <p className="text-sm text-gray-500">
              Restrict to free-tier models (relevant for CommandCode)
            </p>
          </div>
        </label>

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
          />
          <div>
            <p className="font-medium text-gray-900">Enable AI features</p>
            <p className="text-sm text-gray-500">
              Turn on AI campaign designer, smart grouping, and chat assistant
            </p>
          </div>
        </label>

        <div className="flex flex-wrap gap-3 pt-2">
          <Button onClick={handleSave} loading={loading}>
            Save AI Settings
          </Button>
          <Button variant="secondary" onClick={handleTest}>
            Test Connection
          </Button>
          {settings?.source === 'tenant' && (
            <Button variant="secondary" onClick={handleReset} loading={loading}>
              Reset to Platform Defaults
            </Button>
          )}
        </div>

        {testResult && (
          <div className={`p-3 rounded-lg text-sm ${testResult.includes('verified') || testResult.includes('Connection verified') ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-800'}`}>
            {testResult}
          </div>
        )}
      </div>
    </Card>
  );
}
